/**
 * Load and save a project's daily plans.
 *
 * There is NO new table and no new endpoint here. The library is a project object:
 * `gp50.project_objects` is already keyed by (project_id, date, object_name) and already served by
 * `GET/POST /api/projects/object`, which upserts on that key. `object_name = 'schedule'` is all
 * that was needed, alongside the existing 'header', 'target', 'video_sources' and
 * 'performance_filters' objects.
 *
 * THE DATE COLUMN IS A CONSTANT. A schedule is a daily plan — the same running order is reused
 * from one day to the next — so it is stored ONCE per project, not once per day. The key needs a
 * date all the same, and the codebase already has a convention for a project-wide object: the
 * project `header` is stored at '1970-01-01' (see pages/ProjectInfo.tsx and persistantStore's
 * loadProjectHeader). The schedule uses the same row, which is why this file takes no day at all
 * and the API contract did not have to change.
 *
 * Consequences worth knowing:
 *  - The library is PER PROJECT and shared by everyone on it. Two people editing at once is
 *    last-write-wins across the WHOLE library, exactly as it is for the other project objects.
 *  - Saving needs 'write' permission on the project (check_permissions in
 *    server_app/controllers/projects.js); reading needs 'read'. A read-only user sees the
 *    countdown and cannot change it, which is the behaviour the ops room wants.
 */

import { apiEndpoints } from '@config/env';
import { getData, postData } from '@utils/global';
import { error as logError, debug as logDebug } from '@utils/console';
import {
    SCHEDULE_OBJECT_NAME,
    buildScheduleObjectJson,
    parseScheduleObject,
    type SchedulePlan,
    type ScheduleObject,
} from '@utils/scheduleCountdown';

/**
 * The date every schedule object is stored under.
 *
 * Not "today" and not the sidebar's date: see the file header. Changing it would orphan every
 * schedule already saved.
 */
export const SCHEDULE_OBJECT_DATE = '1970-01-01';

export interface ScheduleRequest {
    className: string;
    projectId: number | string;
}

/**
 * Fetch a project's schedule library.
 *
 * A project with no schedule yet is a 204 from the API, which `getData` reports as
 * success === false. That is NOT an error and must not surface as one — it is simply an empty
 * library, which is the state every project starts in. Only a genuine transport/server failure
 * throws.
 */
export async function fetchSchedule(
    req: ScheduleRequest,
    signal?: AbortSignal,
): Promise<ScheduleObject> {
    const empty: ScheduleObject = { version: 2, plans: [], activePlanId: null };
    const { className, projectId } = req;
    if (!className || projectId === null || projectId === undefined) {return empty;}

    const url =
        `${apiEndpoints.app.projects}/object` +
        `?class_name=${encodeURIComponent(className)}` +
        `&project_id=${encodeURIComponent(String(projectId))}` +
        `&date=${encodeURIComponent(SCHEDULE_OBJECT_DATE)}` +
        `&object_name=${encodeURIComponent(SCHEDULE_OBJECT_NAME)}`;

    const response = await getData(url, signal);
    if (!response?.success || response.data === null || response.data === undefined) {
        logDebug('scheduleService: no schedule stored for project', { projectId });
        return empty;
    }
    return parseScheduleObject(response.data);
}

export interface SaveScheduleResult {
    ok: boolean;
    /** Present when ok === false; already human-readable. */
    message?: string;
    /** What was actually stored, after the blank-row drop and the per-plan sort. */
    saved: SchedulePlan[];
    /** The active plan as stored, which may differ from the one asked for if it was deleted. */
    activePlanId: string | null;
}

/**
 * Save the whole library.
 *
 * The WHOLE library, not one plan: the object is a single jsonb value, so a partial write is not a
 * thing the storage can express, and pretending otherwise at this level would only hide the
 * last-write-wins behaviour the page has to live with anyway.
 *
 * Returns the cleaned plans rather than the caller's draft, so the page can adopt exactly what is
 * now in the database — otherwise a draft with a blank trailing row would stay "dirty" forever,
 * because the stored object drops that row and the next dirty check would never agree.
 */
export async function saveSchedule(
    req: ScheduleRequest,
    plans: readonly SchedulePlan[],
    activePlanId: string | null,
    signal?: AbortSignal,
): Promise<SaveScheduleResult> {
    const { className, projectId } = req;
    if (!className || projectId === null || projectId === undefined) {
        return { ok: false, message: 'No project selected.', saved: [], activePlanId: null };
    }

    const json = buildScheduleObjectJson(plans, activePlanId);
    const stored = parseScheduleObject(json);

    try {
        const response = await postData(
            `${apiEndpoints.app.projects}/object`,
            {
                class_name: className,
                project_id: projectId,
                date: SCHEDULE_OBJECT_DATE,
                object_name: SCHEDULE_OBJECT_NAME,
                json,
            },
            signal,
        );

        if (response?.success) {
            logDebug('scheduleService: saved schedule', {
                projectId,
                plans: stored.plans.length,
            });
            return { ok: true, saved: stored.plans, activePlanId: stored.activePlanId };
        }
        return {
            ok: false,
            message: response?.message || 'Failed to save the schedule.',
            saved: stored.plans,
            activePlanId: stored.activePlanId,
        };
    } catch (err) {
        if ((err as { name?: string })?.name === 'AbortError') {throw err;}
        logError('scheduleService: error saving schedule', err as never);
        return {
            ok: false,
            message: (err as { message?: string })?.message || 'Failed to save the schedule.',
            saved: stored.plans,
            activePlanId: stored.activePlanId,
        };
    }
}
